import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from '../common/MarketingHeader';
import MarketingFooter from '../common/MarketingFooter';
import CtaBand from '../Home/CtaBand';
import { FAQ, FEATURES, STEPS, TABLE_SIZES, TIPS } from './content';
import SeatingTry from './demo/SeatingTry';
import '../../../styles/marketing.css';
import '../../../styles/marketing-guide.css';

// Données structurées de la FAQ (résultats enrichis Google), depuis le même contenu
const FAQ_LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
});

// Page « Plan de table mariage en ligne gratuit » : présente l'outil, puis un
// guide (taille des tables, conseils de placement) et une FAQ. Pré-rendue au build.
const SeatingGuidePage: React.FC = () => {
  const t = useT();
  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <section className="mk-hero mk-container">
          <div className="mk-hero-text">
            <span className="mk-eyebrow">Plan de table mariage</span>
            <h1 className="mk-hero-title">
              Votre plan de table de mariage <em>en ligne, gratuit.</em>
            </h1>
            <p className="mk-lead">
              Posez vos tables, glissez vos invités, et le traiteur reçoit les menus et les allergies table par table.
              Sans Excel, sans papier, et toujours à jour avec les réponses de vos invités.
            </p>
            <div className="mk-cta-row">
              <Link to="/signup" className="mkt-btn primary mk-btn-lg">
                Faire mon plan de table
              </Link>
            </div>
            <p className="mk-note">100 % gratuit : toutes les fonctionnalités, sans abonnement ni carte bancaire.</p>
          </div>
        </section>

        <SeatingTry />

        <section className="mk-band" aria-labelledby="mg-how-title">
          <div className="mk-section mk-container">
            <div className="mk-section-head">
              <span className="mk-eyebrow">Comment ça marche</span>
              <h2 id="mg-how-title" className="mk-h2">
                Votre plan de table en trois étapes
              </h2>
            </div>
            <ol className="mk-steps-grid">
              {STEPS.map((step, i) => (
                <li key={step.title} className="mk-step">
                  <span className="mk-step-num" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mk-section mk-container" aria-labelledby="mg-features-title">
          <div className="mk-section-head">
            <span className="mk-eyebrow">Pourquoi en ligne</span>
            <h2 id="mg-features-title" className="mk-h2">
              Mieux qu’un plan de table Excel
            </h2>
            <p className="mg-text">
              Un tableur ou un modèle à imprimer ne savent pas qui a répondu, qui mange végétarien ni qui vient avec ses
              enfants. Ici, le plan de table est relié à votre liste d’invités et à leurs réponses.
            </p>
          </div>
          <div className="mg-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="mg-card">
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mk-band" aria-labelledby="mg-sizes-title">
          <div className="mk-section mk-container mg-guide">
            <div className="mk-section-head">
              <span className="mk-eyebrow">Guide</span>
              <h2 id="mg-sizes-title" className="mk-h2">
                Combien d’invités par table ?
              </h2>
              <p className="mg-text">
                Pour un dîner de mariage, on compte environ 60 cm par convive. Voici les ordres de grandeur pour les
                tables les plus courantes ; vérifiez toujours avec votre lieu ou votre loueur.
              </p>
            </div>
            <table className="mg-table">
              <thead>
                <tr>
                  <th scope="col">Table</th>
                  <th scope="col">Invités conseillés</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_SIZES.map((s) => (
                  <tr key={s.table}>
                    <td>{s.table}</td>
                    <td>{s.guests}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mk-section-head">
              <h2 className="mk-h2">8 conseils pour réussir votre plan de table</h2>
            </div>
            <ol className="mg-tips">
              {TIPS.map((tip) => (
                <li key={tip.title}>
                  <h3>{tip.title}</h3>
                  <p>{tip.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mk-section mk-container mg-guide" aria-labelledby="mg-faq-title">
          <div className="mk-section-head">
            <span className="mk-eyebrow">Questions fréquentes</span>
            <h2 id="mg-faq-title" className="mk-h2">
              Plan de table mariage : vos questions
            </h2>
          </div>
          <div className="mg-faq">
            {FAQ.map(({ q, a }) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_LD }} />
        </section>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default SeatingGuidePage;
